import React from 'react';
import '../../../component-styles/TearSheetPdfDocument.css';
import API_BASE from '../../../utils/api.js';
import { visibleCustomSpecs } from '../../../utils/customSpecs.js';

const proxyImg = (url) =>
  url ? `${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(url)}` : '';

function formatDimensions(item) {
  const parts = [];
  const w = item.width ?? item.dimensions?.width;
  const l = item.length ?? item.dimensions?.length;
  const d = item.depth ?? item.dimensions?.depth;
  const h = item.height ?? item.dimensions?.height;
  if (w) parts.push(`W: ${w}`);
  if (l) parts.push(`L: ${l}`);
  if (d) parts.push(`D: ${d}`);
  if (h) parts.push(`H: ${h}`);
  return parts.join('  ×  ');
}

export default function TearSheetPdfDocument({ items, sheetTitle, company, options = {} }) {
  const {
    showImage = true,
    showRoom = true,
    showDescription = true,
    showVendor = true,
    showMaterials = true,
    showColorFinish = true,
    showDimensions = true,
    showCustomSpecs = true,
    showLink = true,
  } = options;

  const logoUrl = proxyImg(company?.logoUrl);
  const companyName = company?.companyName || 'InteriorFolio';

  const buildFooter = () => {
    const parts = [companyName];
    if (company?.address) parts.push(company.address.replace(/\n/g, ', '));
    if (company?.website) parts.push(company.website);
    if (company?.contactNumber) parts.push(company.contactNumber);
    return parts.join('  |  ');
  };

  return (
    <div className="ts-pdf">
      {items.map((item, idx) => {
        const name = item.clientName || item.name || 'Item';
        const description = item.description;
        const color = item.color;
        const finishes = item.finishes;
        const colorFinish = [color, finishes].filter(Boolean).join(' / ');
        const materials = item.materials;
        const dimensions = formatDimensions(item);
        const vendor = item.vendorName || item.vendor || '';
        const link = item.link || item.productUrl || '';
        const customSpecs = showCustomSpecs ? visibleCustomSpecs(item.customSpecs) : [];

        const hasSpecs =
          (showVendor && vendor) ||
          (showMaterials && materials) ||
          (showColorFinish && colorFinish) ||
          (showDimensions && dimensions) ||
          customSpecs.length > 0 ||
          (showLink && link);

        return (
          <div key={item.itemId || idx} className="ts-pdf__page">
            {/* ── HEADER ── */}
            <div className="ts-pdf__header">
              <div className="ts-pdf__sheet-title">{sheetTitle}</div>
              {logoUrl ? (
                <img
                  className="ts-pdf__logo"
                  src={logoUrl}
                  alt={companyName}
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="ts-pdf__company-name-only">{companyName}</div>
              )}
            </div>

            <div className="ts-pdf__divider" />

            {/* ── IMAGE ── */}
            {showImage && (
              <div className="ts-pdf__img-wrap">
                {item.imageUrl ? (
                  <img
                    className="ts-pdf__img"
                    src={proxyImg(item.imageUrl)}
                    alt={name}
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="ts-pdf__img-placeholder" />
                )}
              </div>
            )}

            {/* ── ITEM INFO ── */}
            {showRoom && item.room && (
              <div className="ts-pdf__room-tag">{item.room}</div>
            )}

            <div className="ts-pdf__item-name">{name}</div>

            {showDescription && description && (
              <div className="ts-pdf__description">{description}</div>
            )}

            {/* ── SPECS ── */}
            {hasSpecs && (
              <div className="ts-pdf__specs">
                {showVendor && vendor && (
                  <div className="ts-pdf__spec-row">
                    <span className="ts-pdf__spec-label">Vendor</span>
                    <span className="ts-pdf__spec-value">{vendor}</span>
                  </div>
                )}
                {showMaterials && materials && (
                  <div className="ts-pdf__spec-row">
                    <span className="ts-pdf__spec-label">Materials</span>
                    <span className="ts-pdf__spec-value">{materials}</span>
                  </div>
                )}
                {showColorFinish && colorFinish && (
                  <div className="ts-pdf__spec-row">
                    <span className="ts-pdf__spec-label">Color / Finish</span>
                    <span className="ts-pdf__spec-value">{colorFinish}</span>
                  </div>
                )}
                {showDimensions && dimensions && (
                  <div className="ts-pdf__spec-row">
                    <span className="ts-pdf__spec-label">Dimensions</span>
                    <span className="ts-pdf__spec-value">{dimensions}</span>
                  </div>
                )}
                {customSpecs.map((spec) => (
                  <div className="ts-pdf__spec-row" key={spec.id}>
                    <span className="ts-pdf__spec-label">{spec.label}</span>
                    <span className="ts-pdf__spec-value">{spec.value}</span>
                  </div>
                ))}
                {showLink && link && (
                  <div className="ts-pdf__spec-row">
                    <span className="ts-pdf__spec-label">Link</span>
                    <span className="ts-pdf__spec-value">
                      <a href={link} target="_blank" rel="noreferrer">View Product</a>
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── FOOTER ── */}
            <div className="ts-pdf__footer">{buildFooter()}</div>
          </div>
        );
      })}
    </div>
  );
}
